import React from 'react';

/**
 * SuitIcon — presentation-only deterministic SVG suit artwork.
 *
 * Replaces Unicode glyphs (♠ ♥ ♦ ♣). iOS Safari renders those codepoints
 * via Apple Color Emoji which ignores declared `font-size` and inflates
 * the character well beyond the intended size (see center-big bug on
 * iPhone 14). SVG makes the visual size deterministic across Safari,
 * Chrome, Firefox and installed PWAs — governed only by the `size`
 * percentage of the parent card. Uses `currentColor` so existing
 * red/black colour inheritance is preserved.
 *
 * Props:
 *   suit  — 'spades' | 'hearts' | 'diamonds' | 'clubs'
 *   size  — any CSS length, e.g. '34%', '18%', '28px'. Default '34%'.
 */
const SUIT_PATHS = {
  // Each path is designed inside a 100x100 viewBox, centered, with
  // visual weight matching a conventional playing-card suit mark.
  spades:
    'M50 8 C 35 32, 10 42, 10 62 C 10 78, 25 86, 36 86 C 44 86, 49 83, 50 80 C 50 83, 46 90, 36 94 L 64 94 C 54 90, 50 83, 50 80 C 51 83, 56 86, 64 86 C 75 86, 90 78, 90 62 C 90 42, 65 32, 50 8 Z',
  hearts:
    'M50 90 C 20 68, 6 50, 6 32 C 6 18, 18 8, 30 8 C 40 8, 46 14, 50 22 C 54 14, 60 8, 70 8 C 82 8, 94 18, 94 32 C 94 50, 80 68, 50 90 Z',
  diamonds:
    'M50 6 L 90 50 L 50 94 L 10 50 Z',
  clubs:
    'M50 8 C 40 8, 32 16, 32 26 C 32 32, 35 37, 39 40 C 32 38, 24 42, 24 52 C 24 62, 32 68, 42 68 C 46 68, 49 67, 50 65 C 50 68, 46 86, 36 94 L 64 94 C 54 86, 50 68, 50 65 C 51 67, 54 68, 58 68 C 68 68, 76 62, 76 52 C 76 42, 68 38, 61 40 C 65 37, 68 32, 68 26 C 68 16, 60 8, 50 8 Z',
};

export default function SuitIcon({ suit, size, className = '', ariaLabel }) {
  const d = SUIT_PATHS[suit];
  if (!d) return null;
  // When `size` is omitted, the parent CSS fully controls width/height
  // (used for the corner `.pip` where CSS applies clamp()). When a
  // percentage/px is provided, it governs the SVG box.
  const style = size != null
    ? { width: size, height: size, display: 'block', flexShrink: 0 }
    : { display: 'block', flexShrink: 0 };
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      style={style}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      focusable="false"
    >
      <path d={d} fill="currentColor" />
    </svg>
  );
}
